import * as React from 'react';
import { connect } from 'react-redux';
import isEmpty from 'lodash/isEmpty';

import Breadcrumbs from 'components/breadcrumbs/Breadcrumbs';

import SOVHeaderPage from 'modules/insights/sov/components/header/page/SOVHeaderPage';
import SOVHeaderReport from 'modules/insights/sov/components/header/report/SOVHeaderReport';
import SOVReportPageCollabary from '../collabary/SOVReportPageCollabary';
import SOVReportPageConsumerInsights from '../consumer-insights/SOVReportPageConsumerInsights';

import { resetPosts } from 'modules/insights/sov/reducers/posts/collabary/sov-posts';

import { isSovTimeframeCanSelectPastTwoWeeksSelector } from 'selectors/sovTimeframeCanSelectPastTwoWeeks';

import { __t, applyTranslations } from 'utils/language';
import { getBrandProfileId } from 'utils/organization';
import { REPORT_SAVED_TIMEOUT } from 'modules/insights/sov/utils/sov';
import { CONSUMER_INSIGHTS } from 'modules/insights/sov/utils/consumer-insights/sov-consumer-insights';

import './sov-report-page.scss';

import type {
  LineGraphEChartsObject,
  LocationWindow,
  Organization
} from 'types';
import type {
  SOVGeneralPageType,
  SOVGeneralProps,
  SOVGeneralVoidFunction,
  SOVReport,
  SOVReportType,
  SOVReportUpdateCallback
} from 'modules/insights/sov/types';

applyTranslations(require('./sov-report-page.i18n.json'));

type OwnProps = {
  params: SOVGeneralProps,
  location: LocationWindow,
  pageType: SOVGeneralPageType,
  reportType: SOVReportType
};

type StateProps = {
  organization: Organization,
  brandProfileId: number | undefined | null,
  report: SOVReport,
  hasAnalysisBeenRun: boolean,
  isAnalysisDownloading: boolean,
  sovTimeframeCanSelectPastTwoWeeks: boolean
};

type DispatchProps = {
  dispatchResetPosts: () => Promise<void>
};

type Props = OwnProps & StateProps & DispatchProps;

type State = {
  isReportSaved: boolean,
  graphInstance: LineGraphEChartsObject | undefined | null
};

const MSG_KEY = 'sov_report_page';

class SOVReportPage extends React.PureComponent<Props, State> {
  state: State = {
    isReportSaved: false,
    graphInstance: null
  };

  isSavedTimer?: number;

  componentWillUnmount() {
    const { dispatchResetPosts } = this.props;

    this.handleClearReportSavedTimer();

    dispatchResetPosts();
  }

  getGraphImageUrl(): string {
    const { graphInstance } = this.state;

    return graphInstance
      ? graphInstance.getDataURL({ backgroundColor: '#fff' })
      : '';
  }

  handleClearReportSavedTimer = (): void => {
    if (this.isSavedTimer) {
      clearTimeout(this.isSavedTimer);
    }
  };

  handleChartInstanceCallback = (echartsInstance: LineGraphEChartsObject): void => {
    this.setState({
      graphInstance: echartsInstance
    });
  };

  handleReportSavedText = (): void => {
    this.setState({
      isReportSaved: true
    }, () => {
      this.isSavedTimer = window.setTimeout(() => {
        this.setState({
          isReportSaved: false
        });
      }, REPORT_SAVED_TIMEOUT);
    });
  };

  renderPageHeader = (): JSX.Element => {
    const { reportType } = this.props;

    return (
      <div className="sov-report-page__header--page">
        <SOVHeaderPage
          reportType={reportType}
        />
      </div>
    );
  };

  renderReportHeader = (
    onUpdateReport: SOVReportUpdateCallback,
    onDownloadReport?: SOVGeneralVoidFunction | null
  ): JSX.Element => {
    const { report, hasAnalysisBeenRun, isAnalysisDownloading } = this.props;
    const { isReportSaved } = this.state;
    const graphImageUrl = this.getGraphImageUrl();

    return (
      <div className="sov-report-page__header--report">
        <SOVHeaderReport
          report={report}
          isReportSaved={isReportSaved}
          isDownloadDisabled={!hasAnalysisBeenRun || isEmpty(graphImageUrl)}
          isAnalysisDownloading={isAnalysisDownloading}
          onUpdateReport={onUpdateReport}
          onDownloadReport={onDownloadReport}
          onClearReportSavedTimer={this.handleClearReportSavedTimer}
        />
      </div>
    );
  };

  renderBreadcrumbs = (): JSX.Element => {
    const { report } = this.props;

    return (
      <div className="sov-report-page__breadcrumbs">
        <Breadcrumbs
          items={[
            {
              label: __t(`${MSG_KEY}.labels.report_list`),
              link: '/insights/sov'
            },
            {
              label: report.name || '',
              link: ''
            }
          ]}
        />
      </div>
    );
  };

  renderConsumerInsights(): React.ReactNode {
    const {
      params,
      location,
      report,
      reportType,
      sovTimeframeCanSelectPastTwoWeeks
    } = this.props;

    const { isReportSaved, graphInstance } = this.state;
    const reportId = Number(params.reportId);

    return (
      <SOVReportPageConsumerInsights
        location={location}
        brandProfileId={report.brandProfileId}
        report={report}
        reportId={reportId}
        reportType={reportType}
        graphInstance={graphInstance}
        isReportSaved={isReportSaved}
        sovTimeframeCanSelectPastTwoWeeks={sovTimeframeCanSelectPastTwoWeeks}
        onReportSavedText={this.handleReportSavedText}
        onClearReportSavedTimer={this.handleClearReportSavedTimer}
        onChartInstanceCallback={this.handleChartInstanceCallback}
      />
    );
  }

  renderCollabary(): React.ReactNode {
    const {
      params,
      organization,
      location,
      brandProfileId,
      report,
      reportType,
      isAnalysisDownloading,
      sovTimeframeCanSelectPastTwoWeeks
    } = this.props;

    const { isReportSaved, graphInstance } = this.state;
    const reportId = Number(params.reportId);

    return (
      <SOVReportPageCollabary
        organization={organization}
        location={location}
        brandProfileId={brandProfileId}
        report={report}
        reportId={reportId}
        reportType={reportType}
        graphInstance={graphInstance}
        isReportSaved={isReportSaved}
        isAnalysisDownloading={isAnalysisDownloading}
        sovTimeframeCanSelectPastTwoWeeks={sovTimeframeCanSelectPastTwoWeeks}
        onRenderBreadcrumbs={this.renderBreadcrumbs}
        onRenderPageHeader={this.renderPageHeader}
        onRenderReportHeader={this.renderReportHeader}
        onReportSavedText={this.handleReportSavedText}
        onChartInstanceCallback={this.handleChartInstanceCallback}
      />
    );
  }

  render(): React.ReactNode {
    const { pageType } = this.props;

    return (
      <div className="sov-report-page">
        {
          pageType === CONSUMER_INSIGHTS
            ? this.renderConsumerInsights()
            : this.renderCollabary()
        }
      </div>
    );
  }
}

const mapStateToProps = (
  {
    runtimeConfig,
    organizations,
    account,
    sovReports,
    sovReportsConsumerInsights,
    sovAnalysis
  },
  ownProps: OwnProps
): StateProps => ({
  organization: organizations.currentOrganization,
  brandProfileId: getBrandProfileId(organizations.currentOrganization),
  hasAnalysisBeenRun: sovAnalysis.analysisHasBeenRun,
  isAnalysisDownloading: sovAnalysis.isDownloading,

  report: ownProps.pageType === CONSUMER_INSIGHTS
    ? sovReportsConsumerInsights.report
    : sovReports.report,

  sovTimeframeCanSelectPastTwoWeeks: isSovTimeframeCanSelectPastTwoWeeksSelector({ runtimeConfig, account })
});

const mapDispatchToProps = (dispatch): DispatchProps => ({
  dispatchResetPosts: () => (
    dispatch(resetPosts())
  )
});

export default connect(mapStateToProps, mapDispatchToProps)(SOVReportPage);

